import {Container, ThemeProvider, createTheme, Box, Button, Dialog, DialogContent, useTheme, Skeleton, Paper, Stack} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import {useEffect, useState} from "react";
// import PropertyGallery from "../components/PropertyGallery";
import PropertyGalleryHeader from "../components/PropertyGalleryHeader";
import PropertyHeader from "../components/PropertyHeader";
import PropertyOverview from "../components/PropertyOverview";
import PropertyDetails from "../components/PropertyDetails";
import Amenities from "../components/Amenities";
import NearbyPlaces from "../components/NearbyPlaces";
import SimilarProperties from "../components/SimilarProperties";
import AgentContact from "../components/AgentContact";
import {useContentByEmbed, useSimilarProperties} from "../../../hooks";
import type { SimilarProperty } from "../data/propertyData";

// Tema personalizado para PropertyPage
const propertyPageTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: "#0e2340",
            light: "#1a3a5c",
            dark: "#0a1a2e",
            contrastText: "#ffffff",
        },
        secondary: {
            main: "#d4a574",
            light: "#e0b890",
            dark: "#b8905a",
            contrastText: "#ffffff",
        },
        background: {
            default: "#ffffff",
            paper: "#ffffff",
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h3: {
            fontWeight: 700,
        },
        h4: {
            fontWeight: 600,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    borderRadius: 8,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },
    },
});

// Componentes Skeleton
function PropertyGalleryHeaderSkeleton() {
    return (
        <Box sx={{ mb: 4, width: "100%", maxWidth: 1400, margin: "0 auto" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                    gridTemplateRows: { xs: "auto", sm: "1fr 1fr" },
                    gap: { xs: "6px", sm: "8px" },
                    height: { xs: 200, sm: 350, md: 350, lg: 400 },
                    borderRadius: "12px",
                    overflow: "hidden",
                }}
            >
                <Skeleton variant="rectangular" width="100%" height="100%" />
                <Skeleton variant="rectangular" width="100%" height="100%" />
                <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
        </Box>
    );
}

function PropertyHeaderSkeleton() {
    return (
        <Box p={3} sx={{ mb: 4, width: "100%" }}>
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 4, alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="60%" height={24} />
                </Box>
                <Box sx={{ display: "flex", gap: 4 }}>
                    <Box sx={{ textAlign: "center" }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1, mx: "auto" }} />
                        <Skeleton variant="text" width={60} height={20} />
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1, mx: "auto" }} />
                        <Skeleton variant="text" width={60} height={20} />
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1, mx: "auto" }} />
                        <Skeleton variant="text" width={60} height={20} />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", alignItems: "center", gap: 2 }}>
                <Skeleton variant="text" width="60%" height={50} />
                <Skeleton variant="text" width="80%" height={24} />
                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 2 }}>
                    <Box sx={{ textAlign: "left" }}>
                        <Skeleton variant="circular" width={32} height={32} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width={50} height={20} />
                    </Box>
                    <Box sx={{ textAlign: "center", flex: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} sx={{ mb: 1, mx: "auto" }} />
                        <Skeleton variant="text" width={50} height={20} sx={{ mx: "auto" }} />
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                        <Skeleton variant="circular" width={32} height={32} sx={{ mb: 1, ml: "auto" }} />
                        <Skeleton variant="text" width={50} height={20} sx={{ ml: "auto" }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function PropertyContentSkeleton() {
    return (
        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 66.666%" }, minWidth: 0 }}>
            {/* Property Overview Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="text" width="40%" height={32} />
                </Box>
                <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={24} sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" }, gap: 2 }}>
                    {[...Array(6)].map((_, i) => (
                        <Paper key={i} elevation={0} sx={{ p: 2.5, backgroundColor: "#f9f4f1", borderRadius: 2 }}>
                            <Skeleton variant="circular" width={36} height={36} sx={{ mb: 1, mx: "auto" }} />
                            <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5, mx: "auto" }} />
                            <Skeleton variant="text" width="80%" height={24} sx={{ mx: "auto" }} />
                        </Paper>
                    ))}
                </Box>
            </Paper>

            {/* Property Details Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="text" width="30%" height={32} />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 1 }}>
                    {[...Array(9)].map((_, i) => (
                        <Paper key={i} elevation={0} sx={{ p: 2.5, backgroundColor: "#f9f4f1", borderRadius: 2, minHeight: 140 }}>
                            <Skeleton variant="circular" width={36} height={36} sx={{ mb: 1, mx: "auto" }} />
                            <Skeleton variant="text" width="70%" height={16} sx={{ mb: 0.5, mx: "auto" }} />
                            <Skeleton variant="text" width="50%" height={24} sx={{ mx: "auto" }} />
                        </Paper>
                    ))}
                </Box>
            </Paper>

            {/* Amenities Skeleton */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="text" width="35%" height={32} />
                </Box>
                <Stack spacing={2}>
                    {[...Array(2)].map((_, i) => (
                        <Paper key={i} elevation={0} sx={{ p: 3, backgroundColor: "#f9f4f1", borderRadius: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                                <Skeleton variant="circular" width={28} height={28} />
                                <Skeleton variant="text" width="30%" height={24} />
                            </Box>
                            {[...Array(4)].map((_, j) => (
                                <Box key={j} sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                                    <Skeleton variant="circular" width={20} height={20} />
                                    <Skeleton variant="text" width="70%" height={20} />
                                </Box>
                            ))}
                        </Paper>
                    ))}
                </Stack>
            </Paper>
        </Box>
    );
}

function AgentContactSkeleton() {
    return (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
                <Skeleton variant="circular" width={120} height={120} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="50%" height={20} />
            </Box>
            <Box sx={{ p: 2, backgroundColor: "#f9f4f1", borderRadius: 2 }}>
                <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={20} />
            </Box>
        </Paper>
    );
}

export default function PropertyPage({
                                         embedId,
                                         encriptedString,
                                         dataConfig,
                                     }:any) {

    const {
        data: contentData,
        isLoading: isLoadingContent,
        error: contentError
    } = useContentByEmbed(embedId, encriptedString)
    console.log('dataConfig', dataConfig);

    useEffect(() => {
        console.log('PropertyPage contentData', contentData)
    }, [contentData])

    // Obtener photoDefault del dataConfig (similar a RealtorWidget)
    const photoDefault = dataConfig?.config?.photoDefault || '';
    const defaultBrokerId = dataConfig?.config?.brokerId || ''
    console.log('photoDefault', photoDefault);

    // Función helper para aplicar photoDefault cuando una imagen no tiene valor
    const getImageWithFallback = (image: string | null | undefined): string => {
        if (image && image !== null && image !== '') {
            return image
        }
        return photoDefault
    }

    // Función para mapear los datos de la API al formato esperado
    const mapApiDataToPropertyData = (apiData: any) => {
        if (!apiData?.data?.data?.[0]) {
            return null; // No data available
        }

        const property = apiData.data.data[0];
        const addressObj = property.addressObject || {};
        
        // Get city, state, and postal code
        const city = addressObj.city || property.city || '';
        const state = addressObj.state || property.state || '';
        const postalCode = addressObj.postalCode || property.postalCode || '';
        
        // Get street address from property.address or construct from addressObject
        const streetAddress = property.address?.trim() || 
            `${addressObj.streetNumberText || ''} ${addressObj.streetName || ''} ${addressObj.unit || ''}`.trim();
        
        // Check if streetAddress already includes city and state
        const hasCityState = city && state && streetAddress.includes(city) && streetAddress.includes(state);
        
        // Build full address - always include city and state if available and not already included
        let fullAddress = streetAddress;
        if (!hasCityState) {
            const addressParts = [streetAddress];
            if (city) addressParts.push(city);
            if (state) addressParts.push(state);
            if (postalCode) addressParts.push(postalCode);
            fullAddress = addressParts.join(', ').trim();
        }

        // Formatear precio
        const formattedPrice = property.listPrice 
            ? `$${property.listPrice.toLocaleString('en-US')}` 
            : null;

        // Formatear área
        const formattedSqft = property.area 
            ? `${property.area.toLocaleString('en-US')} Sq Feet` 
            : null;

        // Calcular días desde listDate
        const listDate = property.listDate ? new Date(property.listDate) : null;
        const daysAgo = listDate 
            ? Math.floor((Date.now() - listDate.getTime()) / (1000 * 60 * 60 * 24))
            : null;
        const listedText = daysAgo !== null 
            ? `Listed ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`
            : null;

        // Parsear features de interior y exterior
        const interiorFeatures = property.interiorFeatures 
            ? property.interiorFeatures.split(',').map((f: string) => f.trim())
            : [];
        const exteriorFeatures = property.exteriorFeatures 
            ? property.exteriorFeatures.split(',').map((f: string) => f.trim())
            : [];

        // Mapear detalles
        const details: Record<string, string | number> = {};
        if (property.bedrooms) details.Bedrooms = property.bedrooms;
        if (property.bathrooms) details.Bathrooms = property.bathrooms;
        if (property.area) details["Square Feet"] = property.area;
        if (property.yearBuilt) details["Year Built"] = property.yearBuilt;
        if (property.subTypeText || property.propertyType) {
            details["Property Type"] = property.subTypeText || property.propertyType;
        }
        if (property.cooling) details.Cooling = property.cooling;
        if (property.heating) details.Heating = property.heating;

        // Agregar HOA si existe
        if (property.association?.fee) {
            const frequency = property.association.frequency || 'Monthly';
            details["HOA Fees"] = `$${property.association.fee}/${frequency === 'Monthly' ? 'mo' : 'yr'}`;
        }

        // Agregar parking/garage
        if (property.garageSpaces) {
            details.Parking = `${property.garageSpaces} ${property.garageSpaces === 1 ? 'Space' : 'Spaces'}`;
        }

        // Agregar MLS
        if (property.listingId) {
            details["MLS #"] = property.listingId;
        }

        // Agregar lot size si existe
        if (property.lotSize) {
            details["Lot Size"] = property.lotSize;
        }

        // Mapear amenities
        const amenities: Record<string, string[]> = {};
        if (interiorFeatures.length > 0) {
            amenities.Interior = interiorFeatures;
        }
        if (exteriorFeatures.length > 0) {
            amenities.Exterior = exteriorFeatures;
        }
        if (property.subdivision) {
            amenities.Community = [property.subdivision];
        }

        // Mapear nearby (solo escuelas por ahora)
        const nearby: Record<string, any[]> = {};
        if (property.school) {
            const schools = [];
            if (property.school.elementarySchool) {
                schools.push({ name: property.school.elementarySchool, distance: "" });
            }
            if (property.school.middleSchool) {
                schools.push({ name: property.school.middleSchool, distance: "" });
            }
            if (property.school.highSchool) {
                schools.push({ name: property.school.highSchool, distance: "" });
            }
            if (schools.length > 0) {
                nearby.Schools = schools;
            }
        }

        // Mapear agente
        const agent = property.agent ? {
            name: `${property.agent.firstName || ''} ${property.agent.lastName || ''}`.trim() || "",
            phone: property.agent.phone || "",
            image: getImageWithFallback(property.agent.photo || property.agent.image),
            description: "", // No hay descripción en la API
        } : { name: "", phone: "", image: photoDefault, description: "" };

        return {
            price: formattedPrice || "",
            status: property.mls?.statusText?.toUpperCase() || property.mls?.status?.toUpperCase() || "",
            address: fullAddress || "",
            beds: property.bedrooms || 0,
            baths: property.bathrooms || 0,
            sqft: formattedSqft || "",
            listed: listedText || "",
            views: 0,
            overview: property.remarks || "",
            features: interiorFeatures.slice(0, 4).concat(exteriorFeatures.slice(0, 2)),
            details,
            amenities: Object.keys(amenities).length > 0 ? amenities : {},
            nearby: Object.keys(nearby).length > 0 ? nearby : {},
            similar: [], // Se llenará después con el hook
            agent: agent || { name: "", phone: "", image: photoDefault, description: "" },
            gallery: property.photos && property.photos.length > 0 
                ? property.photos.map((photo: string) => getImageWithFallback(photo))
                : [],
            latitude: property.latitude || property.geo?.lat,
            longitude: property.longitude || property.geo?.lng,
        };
    };

    // Mapear datos de la API
    const data = contentData?.data ? mapApiDataToPropertyData(contentData) : null;
    
    console.log('defaultBrokerId', defaultBrokerId);
    // Extraer coordenadas y URL del virtual tour para el mapa
    const property = contentData?.data?.data?.[0];
    //const propertyId = property?.listingId || property?.id;
    const propertyId = property?.id;
    const latitude = property?.latitude || property?.geo?.lat;
    const longitude = property?.longitude || property?.geo?.lng;
    const brokerId = property?.office?.brokerid;
    console.log('property', property?.office);
    const isDefaultBroker = brokerId === defaultBrokerId;
    const agentId = property?.agent?.id;
    // Extraer URL del virtual tour (puede estar en diferentes campos)
    const virtualTourUrl = property?.virtualTourUrl || property?.virtualTour || property?.tourUrl || property?.virtualTourLink || property?.url;
    
    // Convertir propertyId a número solo cuando esté disponible
    const numericPropertyId = propertyId ? Number(propertyId) : null;
    
    // Obtener propiedades similares - solo se ejecutará cuando numericPropertyId tenga valor
    const {
        data: similarPropertiesData,
        isLoading: isLoadingSimilar,
        error: similarError
    } = useSimilarProperties(
        numericPropertyId,
        embedId, // embedId
        false, // sameZip
        5 // limit
    );

    useEffect(() => {
        console.log('similarPropertiesData', similarPropertiesData)
        console.log('isLoadingSimilar', isLoadingSimilar)
        console.log('similarError', similarError)
    }, [similarPropertiesData, isLoadingSimilar, similarError]);

    // Transformar propiedades similares al formato esperado
    const transformSimilarProperties = (apiData: any): SimilarProperty[] => {
        if (!apiData?.data || !Array.isArray(apiData.data)) {
            return [];
        }

        return apiData.data.map((prop: any) => {
            // Obtener imagen de diferentes campos posibles y aplicar photoDefault si no tiene valor
            const image = getImageWithFallback(
                prop.photos?.[0] || 
                prop.photo || 
                prop.coverImage || 
                prop.imageUrl || 
                prop.images?.[0]
            );

            // Formatear precio
            const price = prop.listPrice 
                ? `$${prop.listPrice.toLocaleString('en-US')}` 
                : '$0';

            // Construir dirección completa
            const address = prop.address || 
                `${prop.streetNumberText || ''} ${prop.streetName || ''} ${prop.unit || ''}, ${prop.city || ''}, ${prop.state || ''} ${prop.postalCode || ''}`.trim();

            // Obtener sqft
            const sqft = prop.area || prop.sqft || prop.squareFeet || 0;
            const sqftFormatted = sqft ? `${sqft.toLocaleString('en-US')} sqft` : '0 sqft';

            return {
                id: Number(prop.id) || 0,
                image,
                price,
                address,
                beds: prop.bedrooms || 0,
                baths: prop.bathrooms || 0,
                sqft: sqftFormatted,
                url: prop.url || ''
            };
        });
    };

    const similarProperties: SimilarProperty[] = similarPropertiesData 
        ? transformSimilarProperties(similarPropertiesData)
        : [];

    const [openDialog, setOpenDialog] = useState(false);

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // Mostrar skeletons mientras carga
    if (isLoadingContent) {
        return (
            <ThemeProvider theme={propertyPageTheme}>
                <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                    <PropertyGalleryHeaderSkeleton />
                    <PropertyHeaderSkeleton />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 3,
                            mt: 1,
                            alignItems: "flex-start",
                        }}
                    >
                        <PropertyContentSkeleton />
                        <Box
                            sx={{
                                display: { xs: "none", md: "block" },
                                flex: { xs: "1 1 100%", md: "0 0 33.333%" },
                                minWidth: 0,
                                position: { md: "sticky" },
                                top: { md: 20 },
                            }}
                        >
                            <AgentContactSkeleton />
                        </Box>
                    </Box>
                </Container>
            </ThemeProvider>
        );
    }

    // Si hay error o no hay datos, no mostrar nada
    if (contentError || !data) {
        return (
            <ThemeProvider theme={propertyPageTheme}>
                <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                    <Box sx={{ textAlign: "center", py: 8 }}>
                        <Box sx={{ color: "text.secondary", fontSize: "1.1rem" }}>
                            {contentError ? "Error loading property" : "No data available"}
                        </Box>
                    </Box>
                </Container>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={propertyPageTheme}>
            <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                    <PropertyGalleryHeader 
                        images={data.gallery} 
                        latitude={latitude}
                        longitude={longitude}
                        address={data.address}
                        virtualTourUrl={virtualTourUrl}
                        photoDefault={photoDefault}
                    />

                    <PropertyHeader data={data} />
                    
                    {/* Two Column Layout */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 3,
                            mt: 1,
                            alignItems: "flex-start",
                        }}
                    >
                        {/* Left Column - Main Content */}
                        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 66.666%" }, minWidth: 0 }}>
                            <PropertyOverview data={data} />
                            <PropertyDetails details={data.details} />
                            <Amenities amenities={data.amenities} />
                            <NearbyPlaces nearby={data.nearby} />
                            <SimilarProperties similar={similarProperties} />
                        </Box>

                        {/* Right Column - Agent Contact */}
                        <Box
                            sx={{
                                display: { xs: "none", md: "block" },
                                flex: { xs: "1 1 100%", md: "0 0 33.333%" },
                                minWidth: 0,
                                position: { md: "sticky" },
                                top: { md: 20 },
                            }}
                        >
                            <AgentContact 
                                agent={data.agent}
                                propertyId={property?.listingId?.toString() || property?.id?.toString() || "123"}
                                propertyName={property?.address || data.address || "Property"}
                                propertyAddress={data.address || property?.address || ""}
                                vendorId={property?.vendorId || 298}
                                embedId={embedId}
                                agentId={agentId}
                                isDefaultBroker={isDefaultBroker}
                            />
                        </Box>
                    </Box>
                </Container>

                {/* Floating Contact Agent Button for mobile */}
                {data.agent && <FloatingContactButton onOpenDialog={handleOpenDialog} />}

                {/* Dialog for Agent Contact on mobile */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            m: { xs: 2, sm: 3 },
                        },
                    }}
                >
                    <DialogContent sx={{ p: 0 }}>
                        <AgentContact 
                            agent={data.agent}
                            propertyId={property?.listingId?.toString() || property?.id?.toString() || "123"}
                            propertyName={property?.address || data.address || "Property"}
                            propertyAddress={data.address || property?.address || ""}
                            vendorId={property?.vendorId || 298}
                            embedId={embedId}
                            agentId={agentId}
                            isDefaultBroker={isDefaultBroker}
                        />
                    </DialogContent>
                </Dialog>
        </ThemeProvider>
    );
}

function FloatingContactButton({ onOpenDialog }: { onOpenDialog: () => void }) {
    const theme = useTheme();
    
    return (
        <Button
            variant="contained"
            startIcon={<PersonIcon />}
            onClick={onOpenDialog}
            sx={{
                display: { xs: "flex", md: "none" },
                position: "fixed",
                bottom: 24,
                right: 24,
                backgroundColor: theme.palette.primary.main,
                color: "white",
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                borderRadius: "50px",
                textTransform: "none",
                fontSize: "1rem",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                zIndex: 1000,
                "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
                },
            }}
        >
            Contact Agent
        </Button>
    );
}
